import React from 'react';
import {
    AdvancedTable,
    AdvancedTableData,
    AdvancedTableHeader,
    Card,
    CustomRadio,
    CustomRadioGroup,
    CustomSwitch,
    CustomSwitchStatus,
    ExamStatus,
    LoaderBtn,
    WarningModal,
} from 'components';
import { IExamDetails } from 'components/models';
import { Button } from 'react-bootstrap';
import { CustomSwitchEvent } from 'components/Common/CustomSwitch/CustomSwitch';
import { formatPhoneNumber, formDetector } from '_helpers';
import { IApiError } from '_helpers/ApiConnector/types';
import styles from './EditExperts.module.scss';
import cn from 'classnames';

const EditExperts = ({
    getExamDraft,
    getExperts,
    initSelectedExperts,
    examDetails,
    experts,
    submitSelectExperts,
    handleClickCancel,
    loading,
    apiError,
}: Props) => {
    const initialSort = {
        sortField: '',
        sortOrder: true,
        searchField: '',
        searchTerm: '',
    };
    const [tableExpertsList, setTableExpertsList] = React.useState<IAvailableExpert[] | null>(null);
    const [currentExpertId, setCurrentExpertId] = React.useState<string | null>(null);
    const [expertRole, setExpertRole] = React.useState<ExpertRole>('member');
    const [invitedExpertIds, setInvitedExpertIds] = React.useState<string[]>([]);
    const [chairmanId, setChairmanId] = React.useState<string | null>(null);
    const [expertRoleModalShow, setExpertRoleModalShow] = React.useState(false);
    const [warningModalShow, setWarningModalShow] = React.useState(false);
    const [expertFilter, setExpertFilter] = React.useState<ExpertFilter>('all');
    const [sort, setSort] = React.useState(initialSort);

    const tableHeaders: AdvancedTableHeader[] = [
        { fieldName: 'fullName', title: 'ФАМИЛИЯ, ИМЯ, ОТЧЕСТВО' },
        { fieldName: 'phone', title: 'ТЕЛЕФОН' },
        { fieldName: 'email', title: 'ПОЧТА' },
        { fieldName: '', columnStyles: { paddingRight: '30px', minWidth: '9rem' }, title: 'Статус' },
        { fieldName: '', title: 'ВЫБОР' },
    ];

    const examExperts = examDetails && examDetails.experts;

    React.useEffect(() => {
        sortTableExperts();
    }, [expertFilter, sort]);

    React.useEffect(() => {
        setTableExpertsList(experts);
        sortTableExperts();
    }, [experts]);

    React.useEffect(() => {
        setTableExpertsList(experts);

        if (getExamDraft) {
            getExamDraft();
        }

        if (!experts) {
            getExperts({});
        }
    }, []);

    React.useEffect(() => {
        if (initSelectedExperts && !invitedExpertIds.length) {
            setInvitedExpertIds(initSelectedExperts.invitedExpertIds);
            setChairmanId(initSelectedExperts.chairmanId);
            formDetector.setChangedInput('chairmanId', `${initSelectedExperts.chairmanId}`);
            setChangedInputs(initSelectedExperts.invitedExpertIds);
        } else if (examExperts) {
            const nExperts = examExperts.filter(x => (x.acceptedInvite !== false)).map(x => x.expertId);
            const nChairmanId = getChairmanId(examExperts);

            setInvitedExpertIds(nExperts);
            setChairmanId(nChairmanId);
            formDetector.setChangedInput('chairmanId', `${nChairmanId}`);
            setChangedInputs(nExperts);
        }
    }, [initSelectedExperts, examExperts]);

    const setChangedInputs = (newIds: string[]) => {
        if (examExperts && newIds.length) {
            examExperts.forEach((el) => {
                formDetector.setChangedInput(el.expertId, `${newIds.indexOf(el.expertId) !== -1}`);
            });

            formDetector.setChangedInput('examExpertsCount', `${newIds.length}`);
        } else if (initSelectedExperts) {
            initSelectedExperts.invitedExpertIds.forEach((id) => {
                formDetector.setChangedInput(id, 'true');
            });

            formDetector.setChangedInput('examExpertsCount', `${newIds.length}`);
        }
    };

    const getChairmanId = (exp: IExamExpert[]) => {
        const chairman = exp.find(x => Boolean(x.chairman && x.acceptedInvite !== false));
        return chairman ? chairman.expertId : '';
    };

    const sortTableExperts = () => {
        if (!experts || !tableExpertsList) return;

        let nExperts = [...experts];
        const { searchField, searchTerm } = sort;

        if (searchField.length) {
            if (searchTerm === '') {
                setSort(initialSort);
                setTableExpertsList(experts);
                return;
            }

            nExperts = nExperts.filter((item) => {
                let value = item[searchField as keyof IAvailableExpert];

                if (!value && searchField === 'fullName') {
                    value = `${item.lastName} ${item.firstName} ${item.middleName}`;
                }

                if (value && (value.toString().indexOf(searchTerm) !== -1 || value.toString().toLocaleLowerCase().indexOf(searchTerm) !== -1)) {
                    return item;
                }

                return undefined;
            });
        }

        if (expertFilter === 'all') {
            setTableExpertsList(nExperts);
        } else if (expertFilter === 'selected') {
            setTableExpertsList(nExperts.filter(item => invitedExpertIds.indexOf(item.id) !== -1));
        } else {
            setTableExpertsList(nExperts.filter(item => item.employee));
        }
    };

    const handleFilterChange = (currentFilter: ExpertFilter) => {
        setExpertFilter(currentFilter);
        setSort(sort);
    };

    const handleExpertSelect = (expertId: string) => (event: CustomSwitchEvent) => {
        if (!event.value) {
            const newInvitedExpertsIds = invitedExpertIds.filter(x => x !== expertId);
            const nChairmanId = chairmanId === expertId ? null : chairmanId;

            setInvitedExpertIds(newInvitedExpertsIds);
            setChairmanId(nChairmanId);
            formDetector.setChangedInput('chairmanId', `${nChairmanId}`);

            if (expertFilter === 'selected') {
                sortTableExperts();
            }

            setChangedInputs(newInvitedExpertsIds);

            return;
        }
        setExpertRoleModalShow(true);
        setCurrentExpertId(expertId);
    };

    const handleSearch = (fieldName: string, value: string) => setSort({
        ...sort,
        searchField: fieldName,
        searchTerm: value,
    });

    const handleExpertRole = (newExpertRole: ExpertRole) => {
        setExpertRole(newExpertRole);
    };

    const handleOrder = async (fieldName: string) => {
        setSort({
            ...sort,
            sortOrder: !sort.sortOrder,
            sortField: fieldName,
        });
    };

    const handleAccessRoleModal = () => {
        if (currentExpertId) {
            const newInvitedExpertsIds = [...invitedExpertIds, currentExpertId];
            setInvitedExpertIds(newInvitedExpertsIds);
            setExpertRoleModalShow(false);

            const employeeExpert = experts ? experts.find(x => (x.id === currentExpertId)) : null; // getEmployee(currentExpertId);
            const nChairmanId = employeeExpert ? employeeExpert.id : null;

            if (expertRole === 'chairman') {
                setChairmanId(nChairmanId);
                setWarningModalShow(!employeeExpert);
            }

            setChangedInputs(newInvitedExpertsIds);
            formDetector.setChangedInput('chairmanId', `${nChairmanId}`);

            setExpertRole('member');
        }
    };

    const handleCancelModal = () => {
        setWarningModalShow(false);
        setExpertRoleModalShow(false);
    };

    const handleAcceptExpert = () => {
        if (chairmanId === null || chairmanId === '' || invitedExpertIds.length < 3) {
            setWarningModalShow(true);
            return;
        }

        submitSelectExperts({
            chairmanId,
            expertIds: invitedExpertIds,
        });
    };

    const handleRowClick = (id: string) => id;

    const hasExpertId = (id: string) => {
        const result = invitedExpertIds.findIndex(item => item === id);
        return result !== -1;
    };

    const renderExpertStatus = (expertId: string) => (
        <span>
            {invitedExpertIds.indexOf(expertId) === -1 ? '' : chairmanId === expertId ? 'Председатель' : 'Член комиссии'}
        </span>
    );

    const expertsToTableData = (): AdvancedTableData[] => React.useMemo(() => (tableExpertsList ? tableExpertsList.map((item) => {
        let status: CustomSwitchStatus = 'default';
        const examExpert = examExperts && examExperts.find(x => x.expertId === item.id);

        if (examExpert && examExpert.acceptedInvite === true && examDetails && examDetails.status === ExamStatus.AwaitingExpertConfirmation) {
            status = 'accept';
        }

        if (examExpert && examExpert.acceptedInvite === false) {
            status = 'reject';
        }

        return {
            id: item.id,
            columns: [
                `${item.lastName} ${item.firstName} ${item.middleName}`,
                formatPhoneNumber(item.phone) || item.phone,
                item.email,
                renderExpertStatus(item.id),
                renderSwitch(item.id, status),
            ],
        };
    }) : []), [tableExpertsList, invitedExpertIds, chairmanId]);

    const renderModelBody = () => {
        if (invitedExpertIds.length < 3) {
            return (
                <>
                    <p>Для завершения создания экспертной комиссии необходимо назначить, как минимум 3-х экспертов.</p>
                    <p>Как минимум, одним из членов экспертной комиссии должен быть назначен эксперт, являющийся сотрудником ЦОК.</p>
                </>
            );
        }
        return (
            <>
                <p>Для завершения создания экспертной комиссии необходимо назначить председателя комиссии.</p>
                <p>Председателем экспертной комиссии может быть только эксперт, являющийся сотрудником ЦОК.</p>
            </>
        );
    };

    const renderSwitch = (expertId: string, status: CustomSwitchStatus) => (
        <div className={styles.switchContainer}>
            <CustomSwitch
              onClick={handleExpertSelect(expertId)}
              name="expert-selector"
              value={hasExpertId(expertId)}
              status={status}
            />
        </div>
    );

    const radios = [
        { label: 'Все', option: 'all' },
        { label: 'Сотрудники ЦОК', option: 'qecEmployees' },
        { label: 'Выбранные эксперты', option: 'selected' },
    ];

    return (
        <Card
          withoutPadding
          title="Назначение экспертов"
          category="Назначение состава экспертной комиссии"
          detectFormChanges={!!examDetails}
          apiError={apiError}
        >
            <CustomRadioGroup
              radios={radios}
              containerClassName={styles.filterBtnContainer}
              radiosGroupName="expert-filter"
              onChangeRadio={option => handleFilterChange(option as ExpertFilter)}
              withoutFormDetect
            />
            <div className="w-100">
                <AdvancedTable
                  data={expertsToTableData()}
                  tableHeaders={tableHeaders}
                  handleOrder={handleOrder}
                  handleRowClick={handleRowClick}
                  handleSearch={handleSearch}
                  tdClassName={styles.tdClassName}
                  placeholderTitle="Ничего не найдено"
                  placeholderText="Пожалуйста, убедитесь, что запрос введен правильно"
                />
            </div>
            <div className={cn(styles.buttons_container)}>
                <LoaderBtn loading={loading} type="submit" onClick={handleAcceptExpert} variant="primary">
                    {initSelectedExperts && initSelectedExperts.invitedExpertIds.length ? 'Сохранить и вернуться' : 'Назначить выбранных'}
                </LoaderBtn>
                <Button onClick={handleClickCancel} variant="outline-secondary">
                    Отмена
                </Button>
            </div>
            <WarningModal
              show={expertRoleModalShow}
              title="Укажите статус эксперта"
              onHide={handleCancelModal}
              onAccess={handleAccessRoleModal}
              titleClassNames={styles.modalTitle}
            >
                <CustomRadio
                  className="mb-4"
                  labelClassName={styles.modal_radio_btn}
                  label="Член экспертной комиссии"
                  name="expert-role"
                  option="member"
                  checked={expertRole === 'member'}
                  onClick={() => handleExpertRole('member')}
                  withoutFormDetect
                />
                <CustomRadio
                  labelClassName={styles.modal_radio_btn}
                  label="Председатель экспертной комиссии"
                  name="expert-role"
                  option="chairman"
                  checked={expertRole === 'chairman'}
                  onClick={() => handleExpertRole('chairman')}
                  withoutFormDetect
                />
            </WarningModal>
            <WarningModal
              show={warningModalShow}
              title={invitedExpertIds.length < 3
                    ? 'Необходимо добавить экспертов'
                    : 'Необходимо назначить председателя'}
              onHide={handleCancelModal}
              onAccess={handleCancelModal}
              titleClassNames={styles.modalTitle}
            >
                {renderModelBody()}
            </WarningModal>
        </Card>
    );
};

export default EditExperts;

export interface IGetExpertsProps {
    onlyQecEmployees?: boolean;
    onlySelected?: boolean;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

export interface ISubmitExpertsProps {
    expertIds: string[];
    chairmanId: string;
}

interface IExamExpert {
    regNumber: string;
    expertId: string;
    fullName: string;
    email: string;
    phone: string;
    region: string;
    active: boolean;
    signed: boolean;
    chairman: boolean;
    acceptedInvite?: boolean | undefined;
}

interface IAvailableExpert {
    id: string;
    regNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    emailConfirmed: boolean;
    registrationCompleted: boolean;
    phone: string;
    email: string;
    regionId: number;
    region: string;
    sovietMember: boolean;
    akMember: boolean;
    nokMember: boolean;
    chairman: boolean;
    employee?: boolean | undefined;
    position?: string | undefined;
    applicantId?: string | undefined;
    qualified?: boolean;
}

interface Props {
    experts: IAvailableExpert[] | null;
    getExperts: (data: IGetExpertsProps) => void;
    submitSelectExperts: (data: ISubmitExpertsProps) => void;
    handleClickCancel: () => void;
    getExamDraft?: () => void;
    initSelectedExperts?: {
        invitedExpertIds: string[];
        chairmanId: string;
    };
    examDetails?: IExamDetails | null;
    loading?: boolean;
    apiError?: IApiError | null;
}

type ExpertFilter = 'all' | 'qecEmployees' | 'selected';
type ExpertRole = 'member' | 'chairman';
